/**
 * SegmentedControl — accessible single-select pill toggle.
 *
 * Pins the artboard's Density / Appearance segmented controls
 * (`.design-incoming/admin-20260522/source/shell.jsx` AccountMenu): a
 * `--bg-sunken` track with equal-width pills; the active pill lifts onto
 * `--bg-surface` with `--shadow-sm`.
 *
 * **Why a real radiogroup (not toggle buttons).** Density/Appearance are
 * single-select, so this implements the WAI-ARIA radio group pattern:
 * `role="radiogroup"` + `role="radio"` + `aria-checked`, with a roving
 * tabindex (only the selected pill is tabbable) and Arrow-key navigation
 * that wraps. That keeps keyboard + screen-reader behavior correct and axe
 * clean — a `<select>` wouldn't match the segmented visual, and kit
 * `<Button>` doesn't compose into a radio.
 *
 * Lives in `entities/` (shared) because the shared `AccountMenu` — rendered
 * by every portal's top pill — consumes it.
 */
import * as React from 'react';
import { Icon } from '@sparta/ui-kit';

type IconName = React.ComponentProps<typeof Icon>['name'];

export interface SegmentedOption<T extends string> {
  readonly id: T;
  readonly label: string;
  /** Optional leading icon (Appearance variant uses sun/moon/monitor). */
  readonly icon?: IconName;
}

export interface SegmentedControlProps<T extends string> {
  readonly options: readonly SegmentedOption<T>[];
  readonly value: T;
  readonly onChange: (id: T) => void;
  /** Accessible group name, e.g. "Density" / "Appearance". */
  readonly ariaLabel: string;
  /** Visually uppercase the labels (Density variant). */
  readonly uppercase?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  uppercase = false,
}: SegmentedControlProps<T>): React.ReactElement {
  const buttonsRef = React.useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.id === value),
  );

  const focusAndSelect = (index: number): void => {
    const target = options[index];
    if (target === undefined) return;
    onChange(target.id);
    buttonsRef.current[index]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (options.length === 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusAndSelect((activeIndex + 1) % options.length);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusAndSelect((activeIndex - 1 + options.length) % options.length);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className="flex gap-0.5 p-0.5 rounded-full bg-[var(--bg-sunken)]"
    >
      {options.map((option, index) => {
        const active = option.id === value;
        return (
          // eslint-disable-next-line @sparta/no-hand-rolled-button -- ADR frontend-0004 exemption: single-select segmented radio (role="radio" + roving tabindex), not a kit Button.
          <button
            key={option.id}
            ref={(el) => {
              buttonsRef.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(option.id)}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-full border-none cursor-pointer text-xs transition-colors ${
              uppercase ? 'uppercase tracking-wide font-bold' : 'font-semibold'
            } ${
              active
                ? 'bg-[var(--bg-surface)] text-[var(--fg-primary)] [box-shadow:var(--shadow-sm)]'
                : 'bg-transparent text-[var(--fg-secondary)]'
            }`}
          >
            {option.icon !== undefined ? (
              <Icon name={option.icon} size={13} aria-hidden />
            ) : null}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
